/** The chat & activity feed — the append-only event stream beside the command bus. */

/**
 * The maximum accepted chat line length in characters (4000) — shared by the client's input
 * `maxLength` and the server's reject. Matches the `maxLength={4000}` cap already used on every
 * freeform prose textarea in the app (`PilotBuildSheet.tsx`, `MechBuildSheet.tsx`) — a chat line is far shorter prose than
 * a notes field, so this limit cannot bite a real chat line; it exists to keep a paste accident
 * or a buggy client from landing an unbounded `chat_post.text` in the durable feed.
 */
export const MAX_CHAT_TEXT_LENGTH = 4000;

// ── Chat & activity feed ─────────────────────────────────────────────────────
//
// The game-scoped, durable, **append-only** event stream: typed **chat**, `/roll` **dice
// cards**, and **system events** (joins/leaves, combat resolutions) interleaved in ONE feed,
// server-stamped and broadcast to every connected `/ws/game` client, persisted to a
// world-folder `feed.jsonl`.
//
// **This is NOT a Command**: no reducer, no inverse, no undo stack. It keeps
// the *spirit* of the command architecture — server-authoritative + server-stamped identity,
// durable, synced — while dropping the one piece an append-only comms log doesn't fit (undo).
//
// The wire is two messages: a client `chat_post { text }` (the author is NEVER asserted — the
// room stamps the socket-bound slot id, mirroring the wire-`actorId` rule) and a server
// `chat_appended { entry }` broadcast to all. A bounded tail (`FEED_TAIL_CAP`) rides
// `game_welcome.feed` so a joining client sees recent history without a separate fetch.

/** The bounded history a joining client loads (last N entries), matching the `LOG_CAP` pattern. */
export const FEED_TAIL_CAP = 200;

/** What kind of feed entry this is — styled per kind, filterable in COMMS's filter menu. */
export type FeedEntryKind = "chat" | "roll" | "system" | "emote";

/**
 * The fields every {@link FeedEntry} carries. `authorSlotId` is **server-stamped** — the
 * socket-bound slot id the room verified, never a client-asserted author (mirrors the
 * wire-`actorId` rule). Display name (callsign / "GM") resolves client-side from the slot.
 */
export type FeedEntryBase = {
  /** A server-minted unique id for the entry (stable across clients). */
  id: string;
  /** The server-stamped slot id of the author (or the slot a system event concerns; "" when none). */
  authorSlotId: string;
  /** Epoch-ms the server stamped on append. */
  ts: number;
}

/** A typed chat line a player/GM posted. */
export type ChatFeedEntry = FeedEntryBase & {
  kind: "chat";
  /** The message body (trimmed, non-empty; server-validated). */
  text: string;
}

/**
 * One **dice pool** of a rolled `/roll` — the `NdM[kh|kl<n>]` half of a single notation term.
 * A `/roll` is compound (`1d20 + 2d6`), so one entry carries an ordered list of these rather
 * than a single scalar `sides`: each pool has its **own** die size, its own recorded draws, its
 * own keep selection, and its own sign. A dice-overlay consumer picks a mesh per group without
 * re-parsing `notation`.
 *
 * Flat `±<int>` terms produce **no** group — they only move {@link RollFeedEntry.total}.
 */
export type RollTermGroup = {
  /** The die size of this pool (the `M` in `NdM`) — every face in `draws` shares it. */
  sides: number;
  /** The individual die faces the server recorded for this pool, in draw order. */
  draws: number[];
  /** Parallel to `draws`: whether each face was kept (true) or dropped by this pool's own keep-high/low. */
  kept: boolean[];
  /** `1` when the term is added, `-1` when subtracted — applied to the pool's KEPT sum as a whole. */
  sign: 1 | -1;
}

/**
 * A `/roll` dice card. The server parses `/roll` notation, rolls it through
 * `@lancer-vtt/rules`' `dice.ts` (recording every draw), and broadcasts this entry. Fields
 * are self-contained primitives — deliberately NOT coupled to `@lancer-vtt/rules`'
 * `DiceDescriptor`, so the wire stays decoupled from the dice-visualization layer (which
 * *visualizes* a recorded roll; this carries the authoritative record).
 */
export type RollFeedEntry = FeedEntryBase & {
  kind: "roll";
  /** The normalized notation rolled (e.g. `"4d6kh3+1d20-2"`). */
  notation: string;
  /**
   * One {@link RollTermGroup} per **dice** term, in notation order — the recorded draws of a
   * compound roll, split by pool so mixed die sizes (`1d20 + 2d6`) each render on their own
   * mesh. Never empty: the parser requires at least one dice term.
   */
  groups: RollTermGroup[];
  /** The final total: each group's kept faces summed, signed per group, plus the flat terms. */
  total: number;
  /**
   * What the roll is for (`"AGILITY"`, `"Assault"`), from a `/roll <notation> # <label>` post;
   * absent when the post named nothing. Trimmed and capped at {@link MAX_ROLL_LABEL_LENGTH}.
   */
  label?: string;
}

/** The longest roll label a `/roll … # <label>` keeps. */
export const MAX_ROLL_LABEL_LENGTH = 40;

/**
 * The closed set of `SystemFeedEntry.event` machine codes. Closed deliberately: the
 * frontend's `feedVisibility` (`commsFilter.ts`)
 * switches on this exhaustively, so adding a sixth event here without also classifying it
 * there is a compile error, not a silently-dropped feed entry — which is exactly how
 * `roll_error` went unrendered end-to-end (the server built it correctly; the client's
 * `undefined`-returning fallthrough discarded it, with nothing making that a loud failure).
 */
export type SystemFeedEvent = "join" | "leave" | "command" | "resolution" | "roll_error" | "refused" | "decision" | "undo" | "redo";

/** A system event — a join/leave, a login/reconnect, a combat resolution, etc. */
export type SystemFeedEntry = FeedEntryBase & {
  kind: "system";
  /**
   * A machine code for the event — one of {@link SystemFeedEvent} — for filtering/styling.
   * `"join"`/`"leave"` are presence noise, deliberately hidden from every player-facing view.
   * `"command"`/`"resolution"` ride the Activity log / Combat results filter options. `"roll_error"` and
   * `"refused"` are private, ephemeral refusal lines sent to their author's socket alone
   * (never persisted, never broadcast) — the client renders both unconditionally, never
   * filterable off. `"refused"` is the GM-only-gate sibling of `"roll_error"`: every non-GM
   * ws message the Game room gates (`game_set_view`, `game_fx`,
   * `game_command`, `game_undo`, `game_redo`, `game_set_password`, `game_check_password`,
   * `game_delete_room`, `game_reset_world`) now speaks instead of silently swallowing the message,
   * on the same private channel `roll_error` proved out. `"decision"` is the **durable outcome
   * line for a resolved cross-actor decision** (Brace, an Overwatch checkpoint offer, an
   * authored reaction offer, …), appended by the encounter room the moment the recorded answer
   * takes effect — regardless of HOW it was answered (the decider's window, the COMMS card, the
   * GM backstop, or the ✕/Cancel dismiss-as-decline path, which is otherwise invisible). The
   * ephemeral pending card (`game_decision`) stays ephemeral; this entry is
   * the persistent record of what was asked and what was chosen. Rides the Combat results filter option.
   * `"undo"`/`"redo"` are the durable "X undid <action>" lines for an encounter undo/redo
   * (`comms-undo-entry`): the feed stays append-only (`decisions/0040`), so an undone card is
   * never edited — the client strikes it by folding these lines' {@link undoneResolutionIds}.
   * Ride the Combat results filter option.
   */
  event: SystemFeedEvent;
  /** The server-composed, display-ready line (e.g. `"NOMAD linked to OMNINET"`). */
  text: string;
  /**
   * Present **only** on a combat-resolution event bridged from the encounter room
   * (`event: "resolution"`). Carries the recorded
   * `ResolvedStep[]` (opaque here — the frontend narrows to `ResolvedStep[]`, exactly
   * like {@link EncounterChangeMessage.resolution}), the resolution `kind` breadcrumb,
   * and a combatant-id → display-name map resolved **server-side** at resolution time
   * (the steps reference combatant ids, and COMMS.app on `/ws/game` has no encounter
   * state to resolve them). The COMMS feed renders this with the combat `ResultCard`;
   * `text` is the plain fallback line. Absent on every non-resolution system event.
   */
  resolution?: {
    steps: unknown[];
    kind?: string;
    names: Record<string, string>;
    /**
     * The server-stamped resolution id — the SAME value this resolution carried on
     * {@link EncounterChangeMessage.resolution} over the encounter socket. This is the whole
     * correlation key: it is what lets COMMS.app hold this card until the dice for this exact
     * resolution have stopped rolling.
     */
    id: string;
    /**
     * **Advisory resolver warnings** (IMMUNE/no-effect, Invisible miss-chance, area-LoS
     * exclusion, …) — presentation-only strings a driver/CLI/UI can show so a player learns
     * *why* a target silently took no effect. Absent when the resolution produced none
     * (never sent as an empty array). Never enters the forward log / `ResolvedStep[]`
     * — this is the **durable** half of the same warnings carried live on
     * {@link EncounterChangeMessage.resolution} — persisted here so a reload/scrollback
     * shows the same card with its warnings intact.
     */
    warnings?: string[];
    /**
     * **The narrator's breadcrumb** (`resolution-narration` 1.1) — the durable twin of
     * {@link EncounterChangeMessage.resolution}'s `about`, passed through unchanged by
     * `GameRoom.emitResolution`. Same structural-mirror reasoning as `steps` above (`shared/`
     * is leaf, can't import `@lancer-vtt/domain`'s `ResolutionAbout`). COMMS.app has no
     * encounter state loaded, so this — not a live lookup — is the narrator's only source for
     * who acted, with what, at whom. **Additive and optional**: absent on every `feed.jsonl`
     * entry persisted before this field existed, and the narrator must degrade to vaguer prose
     * ("attacks" without a weapon name) rather than crash when it, or any field on it, is
     * missing. Already per-viewer-projected by the time it reaches a socket — see
     * `resolutionFor` (`backend/src/encounterRoomCore.ts`), which drops this whole block when
     * `about.actor` is not `visibleTo` that viewer.
     */
    about?: {
      actor: string;
      action?: string;
      /**
       * `area` mirrors `ResolutionAbout`'s own — the fired weapon's Blast/Burst/Line/Cone template (`resolution-narration` 2.1).
       * `melee` likewise — this attack resolved as melee (`comms-attack-reaction-cards` 1.2).
       */
      weapon?: { name: string; id?: string; area?: { kind: "blast" | "burst" | "line" | "cone"; size: number }; melee?: true };
      /** Mirrors `ResolutionAbout.item` — the non-weapon item an activation used, or the drone/mine a deploy minted or set off (`narration-ledger-retirement` 2.2). */
      item?: { name: string; id?: string };
      /** Mirrors `ResolutionAbout.techOption` — the tech option a tech attack fired (`comms-attack-reaction-cards` 1.3). */
      techOption?: { id?: string; name: string; action?: string };
      targets?: string[];
      /** `barrage`/`noBonus` mirror `ResolutionAbout`'s own — this shot is one of a Barrage, and/or is an Auxiliary rider that deals no bonus damage (`resolution-narration` 2.2). */
      barrage?: true;
      /** Mirrors `ResolutionAbout.shot` — which shot of a multi-shot action this was, the COMMS card's group key (`comms-attack-reaction-cards` 1.1). */
      shot?: { action: "barrage" | "skirmish"; index: number };
      noBonus?: true;
      /** Mirrors `ResolutionAbout.auxRider`: this Main's earlier Aux on the same mount was the rider (no bonus damage). */
      auxRider?: { weapon?: { name: string; id?: string } };
      /** Mirrors `ResolutionAbout.reaction`: the reaction this attack spent (Overwatch, Covering Fire, or a granted shot's feature id). */
      reaction?: string;
      /** Mirrors `ResolutionAbout.forced`: the GM forced this action past the rules (`decisions/0209`). */
      forced?: true;
    };
  };
  /**
   * Present only on an `"undo"`/`"redo"` event whose walked undo unit bridged one or more
   * resolution cards: those cards' {@link resolution}`.id`s. An `"undo"` strikes them, a later
   * `"redo"` naming the same ids un-strikes them — the client folds the feed in order
   * (`commsUndo.ts`). Absent when the undone step carried no card (a manual edit, a move).
   */
  undoneResolutionIds?: string[];
}

/**
 * The closed set of `/kiss`-style third-person emote verbs. A **single-
 * member union, deliberately** — one command, one verb, mirroring why {@link SystemFeedEvent} is
 * closed: a second emote later is a compile error at every consumer (this type, the switch in
 * {@link FeedVisibility}-adjacent code, `CommsApp.tsx`, `chat-cli.ts`), not a silent gap. Do not
 * widen this into an emote framework for a single requested verb.
 */
export type EmoteVerb = "kiss";

/**
 * A third-person emote line (`/kiss <target>`) — authored by a player like
 * chat, but rendered as narration about them rather than their speech (italic, no quote/author
 * prefix styling). Posted through the SAME public path as chat (`appendFeedEntry`): persisted to
 * `feed.jsonl`, broadcast to every connected socket, no private/ephemeral carve-out (unlike a
 * malformed `/roll`'s `roll_error`, which is deliberately private — an emote never fails to parse,
 * so there is no error case to shadow that path).
 *
 * **Deliberately carries NO `text` field.** Every other feed entry composes its own display line
 * server-side; this one does not, on purpose: the line names the author by display name, and a
 * display name resolves **client-side** off live slot presence, never off the wire — the
 * wire carries only the server-stamped `authorSlotId`. If this type grew
 * a `text` field, a future consumer reading `entry.text` would silently render a stale/absent name
 * instead of failing to compile. Compose the line client-side (`emoteLine` in
 * `frontend/src/routes/home/emoteLine.ts`) off `authorSlotId` + `target` instead.
 */
export type EmoteFeedEntry = FeedEntryBase & {
  kind: "emote";
  /** The verb performed — closed to {@link EmoteVerb} (one member today: `"kiss"`). */
  emote: EmoteVerb;
  /** The trimmed target string, capped at 80 chars. Absent ⇒ the no-target form ("blew a kiss"). */
  target?: string;
};

/** One entry in the interleaved feed — chat, a roll card, a system event, or an emote. */
export type FeedEntry = ChatFeedEntry | RollFeedEntry | SystemFeedEntry | EmoteFeedEntry;

/**
 * Client→server on `/ws/game`: post a chat line into the game feed. The author is **not**
 * carried — the room stamps the socket-bound slot id (the wire-`actorId` rule). Any
 * authenticated slot may post (NOT GM-gated). The server builds a
 * {@link ChatFeedEntry}, appends it to `feed.jsonl`, and broadcasts {@link ChatAppendedMessage}.
 */
export type ChatPostMessage = {
  type: "chat_post";
  /** The message body the client typed (server trims + rejects empty). */
  text: string;
}

/**
 * Server→clients broadcast: one new {@link FeedEntry} was appended to the game feed (a chat
 * post, a system event, or a roll). Every connected `/ws/game` socket receives it;
 * the client appends it to its in-memory bounded tail.
 */
export type ChatAppendedMessage = {
  type: "chat_appended";
  entry: FeedEntry;
}